import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../../App";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Star, Users, MessageSquare, Mail, MailX, Loader2 } from "lucide-react";
import { RatingScoreCard, RatingDistribution } from "../../components/Reviews";
import OwnerReviewAnalytics from "./OwnerReviewAnalytics";

export function OwnerReviewsPage() {
  const { t } = useTranslation();
  const [data, setData] = useState({ reviews: [], venues: [] });
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/owner/reviews`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  // Build a derived per-venue rating summary so the score card can render
  // even before the public summary endpoint is loaded.
  const summary = useMemo(() => {
    const approved = (data.reviews || []).filter((r) => r.status === "approved" && r.rating);
    const total = approved.length;
    if (!total) return null;
    const dist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let sum = 0;
    for (const r of approved) { dist[r.rating] = (dist[r.rating] || 0) + 1; sum += r.rating; }
    return { average: total ? sum / total : null, total, distribution: dist };
  }, [data.reviews]);

  return (
    <div data-testid="owner-reviews-page" className="space-y-6">
      <h1 className="text-2xl font-medium text-foreground flex items-center gap-2">
        <Star className="h-6 w-6 text-primary" />
        {t("owner:reviews", { defaultValue: "Reviews & Ratings" })}
      </h1>

      {loading && <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin inline" /></div>}

      {!loading && summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <RatingScoreCard summary={summary} />
          <div className="bg-card rounded-xl border border-border p-6">
            <RatingDistribution summary={summary} />
          </div>
        </div>
      )}

      <OwnerReviewAnalytics />

      {!loading && (data.reviews || []).length === 0 && (
        <div className="bg-card rounded-xl border border-border p-12 text-center">
          <Star className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t("owner:reviews_empty", { defaultValue: "You haven't received any reviews yet." })}</p>
        </div>
      )}

      <div className="space-y-4">
        {(data.reviews || []).map((r) => <OwnerReviewCard key={r.review_id} review={r} onChange={refresh} />)}
      </div>
    </div>
  );
}


function OwnerReviewCard({ review, onChange }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [reply, setReply] = useState(review.owner_reply_text || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!reply.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/reviews/${review.review_id}/reply`, { reply_text: reply.trim() });
      toast.success(t("owner:reply_sent", { defaultValue: "Reply sent" }));
      setEditing(false);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 space-y-3" data-testid={`owner-review-${review.review_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground">{review.user_name}</span>
          {review.user_email
            ? <span className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" /> {review.user_email}</span>
            : <span className="text-xs text-muted-foreground flex items-center gap-1"><MailX className="h-3 w-3" /> {t("owner:email_hidden", { defaultValue: "Email hidden by user" })}</span>}
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${review.status === "pending" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
          {review.status}
        </span>
      </div>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((s) => (
          <Star key={s} className={`h-4 w-4 ${s <= (review.rating || 0) ? "fill-yellow-500 text-yellow-500" : "text-muted-foreground/40"}`} />
        ))}
        <span className="text-xs text-muted-foreground ml-2">{review.submitted_at?.slice(0, 10)}</span>
      </div>
      <p className="text-sm text-foreground whitespace-pre-wrap">
        {review.review_text || review.predefined_text_en || review.predefined_text_el}
      </p>

      {!editing && review.owner_reply_text && (
        <div className="bg-muted/40 rounded-lg p-3 border-l-2 border-primary">
          <p className="text-xs font-medium text-primary mb-1 flex items-center gap-1">
            <MessageSquare className="h-3 w-3" /> {t("owner:your_reply", { defaultValue: "Your reply" })}
            {review.owner_reply_status && review.owner_reply_status !== "visible" && (
              <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded bg-amber-100 text-amber-800">{review.owner_reply_status}</span>
            )}
          </p>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{review.owner_reply_text}</p>
        </div>
      )}

      {review.status === "approved" && (
        editing ? (
          <div className="space-y-2">
            <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder={t("owner:reply_placeholder", { defaultValue: "Reply to this review…" })} data-testid={`owner-reply-input-${review.review_id}`} />
            <div className="flex gap-2">
              <Button size="sm" onClick={submit} disabled={saving} data-testid={`owner-reply-save-${review.review_id}`}>
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <MessageSquare className="h-4 w-4 mr-1" />}
                {t("owner:save_reply", { defaultValue: "Save reply" })}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                {t("owner:cancel", { defaultValue: "Cancel" })}
              </Button>
            </div>
          </div>
        ) : (
          <Button size="sm" variant={review.owner_reply_text ? "outline" : "default"} onClick={() => setEditing(true)} data-testid={`owner-reply-btn-${review.review_id}`}>
            <MessageSquare className="h-4 w-4 mr-1" />
            {review.owner_reply_text ? t("owner:edit_reply", { defaultValue: "Edit reply" }) : t("owner:reply", { defaultValue: "Reply" })}
          </Button>
        )
      )}
    </div>
  );
}

export function OwnerTeamPage() {
  const { t } = useTranslation();
  return (
    <div data-testid="owner-team-page">
      <h1 className="text-2xl font-medium text-foreground mb-6">{t("owner:team")}</h1>
      <div className="bg-card rounded-xl border border-border p-6 text-center py-12">
        <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">{t("owner:team_coming_soon")}</p>
      </div>
    </div>
  );
}

export function OwnerSettingsPage() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div data-testid="owner-settings-page">
      <h1 className="text-2xl font-medium text-foreground mb-6">{t("owner:settings")}</h1>
      <div className="bg-card rounded-xl border border-border p-6">
        <Button variant="destructive" onClick={handleLogout}>
          {t("owner:sign_out")}
        </Button>
      </div>
    </div>
  );
}
